import {
    AvailabilityAnnounce,
    NextEpisode,
    Progress,
    MovieViewOption,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';

interface BuilderState {
    viewOption: MovieViewOption | null;
    announce: AvailabilityAnnounce | null;
    progress: Progress | null;
    nextEpisode: NextEpisode | null;
    trailerContentId: string | undefined;
    hasAboutMovie: boolean;
    hasDropdown: boolean;
    hasNotInteresting: boolean;
    hasEmotions: boolean;
}

export interface ContentActionsConfig {
    content: Content;
    actions: Action[];
    note: Note;
}

type AnnounceContent = { type: 'announce' };
type OfferContent = { type: 'offer' };
type ProgressContent = { type: 'progress' };
type NextEpisodeContent = { type: 'nextEpisode' };
type DiscoveryContent = { type: 'discovery' };

type Content =
    | AnnounceContent
    | OfferContent
    | ProgressContent
    | NextEpisodeContent
    | DiscoveryContent
    | null;

type WatchAction = { type: 'watch' };
type OfferAction = { type: 'offer' };
type TrailerAction = { type: 'trailer' };
type AboutMovieAction = { type: 'aboutMovie' };
type PlannedToWatchAction = { type: 'plannedToWatch' };
type NotInterestingAction = { type: 'notInteresting' };
type EmotionsAction = { type: 'emotions' };
type DropdownAction = { type: 'dropdown' };
type DiscoveryAction = { type: 'discovery' };

type Action =
    | WatchAction
    | OfferAction
    | TrailerAction
    | AboutMovieAction
    | PlannedToWatchAction
    | NotInterestingAction
    | EmotionsAction
    | DropdownAction
    | DiscoveryAction;

type ExpirationNote = { type: 'expiration' };
type SubscriptionNote = { type: 'subscription' };
type DisclaimerNote = { type: 'disclaimer' };

type Note = ExpirationNote | SubscriptionNote | DisclaimerNote | null;

interface ContentActionsBuilder {
    withViewOption(viewOption?: MovieViewOption | null): ContentActionsBuilder;
    withAnnounce(announce?: AvailabilityAnnounce | null): ContentActionsBuilder;
    withProgress(progress?: Progress | null): ContentActionsBuilder;
    withNextEpisode(nextEpisode?: NextEpisode | null): ContentActionsBuilder;
    withTrailer(trailerContentId?: string): ContentActionsBuilder;
    withAboutMovie(): ContentActionsBuilder;
    withDropdown(): ContentActionsBuilder;
    withNotInteresting(): ContentActionsBuilder;
    withEmotions(): ContentActionsBuilder;
    build(): ContentActionsConfig;
}

function buildContent(state: BuilderState): Content {
    const hasAnnounce = Boolean(state.announce);

    if (hasAnnounce) {
        return { type: 'announce' };
    }

    const hasViewOption = Boolean(state.viewOption);
    const isPurchasable = Boolean(state.viewOption?.isPurchasable);
    const isPurchased = Boolean(state.viewOption?.isPurchased);

    const isWatchable = (state.viewOption?.isWatchable && state.viewOption?.isPurchased) ?? false;

    if (state.nextEpisode && isWatchable) {
        return { type: 'nextEpisode' };
    }

    if (state.progress && isWatchable) {
        return { type: 'progress' };
    }

    if (isPurchasable) {
        return { type: 'offer' };
    }

    if (hasViewOption && !isWatchable && !isPurchasable && !hasAnnounce && !isPurchased) {
        return { type: 'discovery' };
    }

    return null;
}

function buildNote(state: BuilderState): Note {
    if (
        state.nextEpisode?.availability?.availabilityEndDate &&
        state.nextEpisode?.availability?.availabilityStatus &&
        state.viewOption?.isPurchased
    ) {
        return { type: 'expiration' };
    }

    if (state.viewOption?.disclaimer) {
        return { type: 'disclaimer' };
    }

    if (state.viewOption?.isPurchasable && state.viewOption?.isWatchable) {
        return { type: 'subscription' };
    }

    return null;
}

function buildActions(state: BuilderState): Action[] {
    const actions: Action[] = [];

    const hasViewOption = Boolean(state.viewOption);
    const isWatchable = Boolean(state.viewOption?.isWatchable);
    const isPurchasable = Boolean(state.viewOption?.isPurchasable);
    const hasAnnounce = Boolean(state.announce);
    const isPurchased = Boolean(state.viewOption?.isPurchased);

    if (!hasViewOption && !hasAnnounce) {
        return [];
    }

    // Unavailable content
    if (hasViewOption && !isWatchable && !isPurchasable && !hasAnnounce && !isPurchased) {
        return [{ type: 'discovery' }];
    }

    // first action. main action
    if (hasAnnounce) {
        actions.push({ type: 'plannedToWatch' });
    } else if (isWatchable && isPurchased) {
        actions.push({ type: 'watch' });
    } else if (isPurchasable) {
        actions.push({ type: 'offer' });
    }

    if (state.hasAboutMovie) {
        actions.push({ type: 'aboutMovie' });
    }

    if (isWatchable && !hasAnnounce) {
        actions.push({ type: 'plannedToWatch' });
    }

    if (state.trailerContentId) {
        actions.push({ type: 'trailer' });
    }

    if (state.hasEmotions && isWatchable && isPurchased && !hasAnnounce) {
        actions.push({ type: 'emotions' });
    }

    if (state.hasNotInteresting && (isWatchable || hasAnnounce)) {
        actions.push({ type: 'notInteresting' });
    }

    if (state.hasDropdown && !hasAnnounce) {
        actions.push({ type: 'dropdown' });
    }

    return actions;
}

export function contentActionsBuilder(): ContentActionsBuilder {
    const state: BuilderState = {
        viewOption: null,
        announce: null,
        progress: null,
        nextEpisode: null,
        trailerContentId: undefined,
        hasAboutMovie: false,
        hasDropdown: false,
        hasNotInteresting: false,
        hasEmotions: false,
    };

    return {
        withViewOption(viewOption) {
            state.viewOption = viewOption ?? null;
            return this;
        },
        withAnnounce(announce) {
            if (announce) {
                state.announce = announce;
            }
            return this;
        },
        withProgress(progress) {
            if (progress) {
                state.progress = progress;
            }
            return this;
        },
        withNextEpisode(nextEpisode) {
            if (nextEpisode) {
                state.nextEpisode = nextEpisode;
            }
            return this;
        },
        withTrailer(trailerContentId) {
            if (trailerContentId) {
                state.trailerContentId = trailerContentId;
            }
            return this;
        },
        withAboutMovie() {
            state.hasAboutMovie = true;
            return this;
        },
        withDropdown() {
            state.hasDropdown = true;
            return this;
        },
        withNotInteresting() {
            state.hasNotInteresting = true;
            return this;
        },
        withEmotions() {
            state.hasEmotions = true;
            return this;
        },
        build() {
            return {
                content: buildContent(state),
                actions: buildActions(state),
                note: buildNote(state),
            };
        },
    };
}
